import React, { createContext, useContext, useState, ReactNode } from 'react';
import EscaloneLoading from '../components/EscaloneLoading';
import { Building, Database, Users, FileText, Settings, Workflow } from 'lucide-react';

// Tipos de loading com ícones específicos
export type LoadingType = 
  | 'empresas' 
  | 'fluxos' 
  | 'colaboradores' 
  | 'departamentos' 
  | 'cards' 
  | 'procedimentos' 
  | 'automacoes' 
  | 'users' 
  | 'mongodb'
  | 'auth'
  | 'configuracoes'
  | 'general';

interface LoadingState {
  isLoading: boolean;
  type: LoadingType;
  message: string;
  submessage?: string;
}

interface LoadingContextType {
  loading: LoadingState;
  showLoading: (type: LoadingType, message: string, submessage?: string) => void;
  hideLoading: () => void;
  updateMessage: (message: string, submessage?: string) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

const loadingConfig = {
  empresas: {
    icon: Building,
    defaultMessage: 'Carregando empresas',
    defaultSubmessage: 'Conectando ao Atlas MongoDB...'
  },
  fluxos: {
    icon: Workflow,
    defaultMessage: 'Carregando fluxos',
    defaultSubmessage: 'Obtendo fluxos de trabalho...'
  },
  colaboradores: {
    icon: Users,
    defaultMessage: 'Carregando colaboradores',
    defaultSubmessage: 'Buscando dados dos colaboradores...'
  },
  departamentos: {
    icon: Building,
    defaultMessage: 'Carregando departamentos',
    defaultSubmessage: 'Obtendo estrutura organizacional...'
  },
  cards: {
    icon: FileText,
    defaultMessage: 'Carregando cards',
    defaultSubmessage: 'Buscando cartões do fluxo...'
  },
  procedimentos: {
    icon: FileText,
    defaultMessage: 'Carregando procedimentos',
    defaultSubmessage: 'Obtendo procedimentos operacionais...'
  },
  automacoes: {
    icon: Settings,
    defaultMessage: 'Carregando automações',
    defaultSubmessage: 'Buscando automações configuradas...'
  },
  users: {
    icon: Users,
    defaultMessage: 'Carregando usuários',
    defaultSubmessage: 'Obtendo dados de usuários...'
  },
  mongodb: {
    icon: Database,
    defaultMessage: 'Conectando ao banco',
    defaultSubmessage: 'Estabelecendo conexão com MongoDB...'
  },
  auth: {
    icon: Users,
    defaultMessage: 'Autenticando',
    defaultSubmessage: 'Verificando credenciais...'
  },
  configuracoes: {
    icon: Settings,
    defaultMessage: 'Carregando configurações',
    defaultSubmessage: 'Obtendo configurações do sistema...'
  },
  general: {
    icon: Database,
    defaultMessage: 'Carregando',
    defaultSubmessage: 'Processando solicitação...'
  }
};

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState<LoadingState>({
    isLoading: false,
    type: 'general',
    message: '',
    submessage: ''
  });
  
  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null);
  // Use ReturnType<typeof setTimeout> para funcionar tanto no browser quanto no Node sem depender de NodeJS namespace
  const [hideTimeoutId, setHideTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);

  const showLoading = (type: LoadingType, message?: string, submessage?: string) => {
    const config = loadingConfig[type];
    
    // Limpar timeout anterior se existir
    if (hideTimeoutId) {
      clearTimeout(hideTimeoutId);
      setHideTimeoutId(null);
    }
    
    setLoadingStartTime(Date.now());
    setLoading({
      isLoading: true,
      type,
      message: message || config.defaultMessage,
      submessage: submessage || config.defaultSubmessage
    });
  };

  const hideLoading = () => {
    const currentTime = Date.now();
    const elapsed = loadingStartTime ? currentTime - loadingStartTime : 0;
    const minDuration = 2000; // 2 segundos mínimo
    
    if (elapsed < minDuration) {
      // Se não passou tempo suficiente, aguardar
      const remainingTime = minDuration - elapsed;
      const timeoutId = setTimeout(() => {
        setLoading(prev => ({ ...prev, isLoading: false }));
        setLoadingStartTime(null);
        setHideTimeoutId(null);
      }, remainingTime);
      setHideTimeoutId(timeoutId);
    } else {
      // Se já passou tempo suficiente, esconder imediatamente
      setLoading(prev => ({ ...prev, isLoading: false }));
      setLoadingStartTime(null);
    }
  };

  const updateMessage = (message: string, submessage?: string) => {
    setLoading(prev => ({ ...prev, message, submessage }));
  };

  return (
    <LoadingContext.Provider value={{ loading, showLoading, hideLoading, updateMessage }}>
      {children}
      <LoadingOverlay />
    </LoadingContext.Provider>
  );
};

const LoadingOverlay: React.FC = () => {
  const context = useContext(LoadingContext);
  
  if (!context || !context.loading.isLoading) {
    return null;
  }

  return <EscaloneLoading />;
};

// Hook para usar o loading
export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading deve ser usado dentro de um LoadingProvider');
  }
  return context;
};
